// frontend/src/components/AgentOrchestrator.js
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSpeech } from '../context/SpeechContext';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import { useAgentOrchestrator } from '../hooks/useAgentOrchestrator';

const AgentOrchestrator = () => {
  const location = useLocation();
  const { lastCommand } = useSpeech();
  const { token } = useAuth();
  const {
    handleAgentAction,
    agentMode,
    processLocalAction
  } = useAgentOrchestrator();
  const lastCommandTimestamp = useRef(null);
  const activeRequestRef = useRef(null);

  useEffect(() => {
    const handleCancel = () => {
      activeRequestRef.current?.abort?.();
      activeRequestRef.current = null;
      window.dispatchEvent(new CustomEvent('q-agent-thinking', { detail: false }));
    };
    window.addEventListener('q-agent-cancel', handleCancel);
    return () => window.removeEventListener('q-agent-cancel', handleCancel);
  }, []);

  useEffect(() => {
    if (lastCommand && lastCommand.timestamp !== lastCommandTimestamp.current) {
      lastCommandTimestamp.current = lastCommand.timestamp;

      // 1. Intercepción Local (Bucle de Confirmación)
      if (agentMode !== 'IDLE') {
        const handledLocally = processLocalAction(lastCommand.text);
        if (handledLocally) {
          console.log(`[Agent] Comando manejado localmente (${agentMode}): "${lastCommand.text}"`);
          return;
        }
      }

      const processIntent = async () => {
        if (!token) {
          console.warn('[Agent] Intento de procesar comando sin token de autenticación.');
          // Idealmente, el hook también podría manejar este speech.
          // handleAgentAction({ type: 'ERROR', speech: 'No estás autenticado.' });
          return;
        }

        console.log(`[Agent] Procesando comando: "${lastCommand.text}"`);
        window.dispatchEvent(new CustomEvent('q-agent-thinking', { detail: true }));

        const controller = new AbortController();
        activeRequestRef.current = controller;

        try {
          const response = await fetch(`${API_URL}/api/agent/intent`, {
            method: 'POST',
            signal: controller.signal,
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              text: lastCommand.text,
              currentContext: { route: location.pathname }
            })
          });

          if (!response.ok) {
            throw new Error(`HTTP Error: ${response.status}`);
          }

          const data = await response.json();

          // La lógica compleja se delega al hook.
          handleAgentAction({ ...data, originalText: lastCommand.text });

        } catch (error) {
          if (error.name === 'AbortError') return;
          console.error('[Agent] ERROR CRÍTICO:', error);
          // El hook podría manejar el speech de error.
          handleAgentAction({ type: 'ERROR', speech: `Error de comunicación: ${error.message}` });
        } finally {
          if (activeRequestRef.current === controller) activeRequestRef.current = null;
          window.dispatchEvent(new CustomEvent('q-agent-thinking', { detail: false }));
        }
      };

      processIntent();
    }
  }, [lastCommand, location.pathname, token, handleAgentAction, agentMode, processLocalAction]);

  return null; // Este componente no renderiza nada
};

export default AgentOrchestrator;
